import { parseUnits } from 'viem';
import { wagmiAdapter } from '../config/appkit';
import { getAccount, switchChain, sendTransaction, getChainId, waitForTransactionReceipt } from '@wagmi/core';

export interface EVMTransactionParams {
  chainId: number;
  to?: string; // For backward compatibility
  toAddress: string;
  amount: string; // Should be in base units (wei, token units) when isBaseUnits=true
  tokenAddress?: string;
  isBaseUnits?: boolean; // Indicates if amount is already in base units
  network?: string;
  token?: string;
  decimals?: number;
}

export class EVMTransactionError extends Error {
  constructor(
    message: string,
    public code: 'WRONG_CHAIN' | 'USER_REJECTED' | 'INSUFFICIENT_FUNDS' | 'UNKNOWN',
    public details?: any
  ) {
    super(message);
    this.name = 'EVMTransactionError';
  }
}

/**
 * Check if wallet is connected and on the correct chain
 */
export async function checkWalletConnection(requiredChainId: number) {
  // Get account info from wagmi
  const account = getAccount(wagmiAdapter.wagmiConfig);
  
  if (!account.isConnected) {
    throw new EVMTransactionError(
      'No wallet connected',
      'UNKNOWN'
    );
  }

  const currentChainId = getChainId(wagmiAdapter.wagmiConfig);
  
  if (currentChainId !== requiredChainId) {
    return { needsChainSwitch: true, currentChainId };
  }

  return { needsChainSwitch: false, currentChainId };
}

/**
 * Request chain switch if needed
 */
export async function requestChainSwitch(targetChainId: number): Promise<void> {
  try {
    await switchChain(wagmiAdapter.wagmiConfig, { chainId: targetChainId });
  } catch (error: any) {
    if (error.code === 4902) {
      throw new EVMTransactionError(
        'Chain not added to wallet. Please add the network first.',
        'WRONG_CHAIN',
        { chainId: targetChainId }
      );
    } else if (error.code === 4001) {
      throw new EVMTransactionError(
        'User rejected chain switch',
        'USER_REJECTED'
      );
    } else {
      throw new EVMTransactionError(
        'Failed to switch chain',
        'WRONG_CHAIN',
        error
      );
    }
  }
}

/**
 * Build ERC20 transfer data
 */
function encodeERC20Transfer(toAddress: string, amount: bigint): `0x${string}` {
  // ERC20 transfer function signature: transfer(address,uint256)
  const functionSignature = '0xa9059cbb';
  const paddedAddress = toAddress.slice(2).padStart(64, '0');
  const paddedAmount = amount.toString(16).padStart(64, '0');
  
  return `${functionSignature}${paddedAddress}${paddedAmount}` as `0x${string}`;
}

/**
 * Get token decimals - returns defaults for known tokens
 */
function getTokenDecimals(tokenAddress: string, chainId: number): number {
  // USDC decimals vary by chain
  const usdcAddresses = [
    '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', // Ethereum
    '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', // Base
    '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', // Polygon
    '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', // BNB (18 decimals)
    '0xaf88d065e77c8cC2239327C5EDb3A432268e5831', // Arbitrum
    '0xB97EF9Ef8734C71904D8002F8b6Bc66Dd9c48a6E', // Avalanche
  ].map(addr => addr.toLowerCase());

  const usdtAddresses = [
    '0xdAC17F958D2ee523a2206206994597C13D831ec7', // Ethereum
    '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', // Polygon
    '0x55d398326f99059fF775485246999027B3197955', // BNB (18 decimals)
    '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9', // Arbitrum
    '0x9702230A8Ea53601f5cD2dc00fDBc13d4dF4A8c7', // Avalanche
  ].map(addr => addr.toLowerCase());

  const tokenAddressLower = tokenAddress.toLowerCase();

  // Special case for BNB chain USDC/USDT (18 decimals)
  if (chainId === 56) {
    if (tokenAddressLower === '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d' || 
        tokenAddressLower === '0x55d398326f99059ff775485246999027b3197955') {
      return 18;
    }
  }

  // Default USDC/USDT to 6 decimals on other chains
  if (usdcAddresses.includes(tokenAddressLower) || usdtAddresses.includes(tokenAddressLower)) {
    return 6;
  }

  // Default to 18 decimals for unknown tokens
  return 18;
}

/**
 * Send EVM transaction (native or ERC20)
 */
export async function sendEVMTransaction({
  chainId,
  toAddress,
  amount,
  tokenAddress,
  isBaseUnits = false
}: EVMTransactionParams): Promise<string> {
  try {
    console.log('🚀 sendEVMTransaction called with:', {
      chainId,
      toAddress,
      amount,
      tokenAddress,
      isBaseUnits
    });

    // Check wallet connection and chain
    const { needsChainSwitch } = await checkWalletConnection(chainId);
    
    if (needsChainSwitch) {
      console.log('🔄 Chain switch needed, switching to:', chainId);
      await requestChainSwitch(chainId);
    }

    // Get account
    const account = getAccount(wagmiAdapter.wagmiConfig);
    console.log('📱 Account info:', { 
      address: account.address, 
      isConnected: account.isConnected,
      connector: account.connector?.name 
    });
    
    if (!account.address) {
      throw new EVMTransactionError('No wallet address available', 'UNKNOWN');
    }

    let txHash: string;

    if (tokenAddress) {
      // ERC20 transfer
      const decimals = getTokenDecimals(tokenAddress, chainId);
      const amountInBaseUnits = isBaseUnits 
        ? BigInt(amount)
        : parseUnits(amount, decimals);
      
      const data = encodeERC20Transfer(toAddress, amountInBaseUnits);
      
      console.log('💰 Sending ERC20 transaction:', {
        to: tokenAddress,
        data: data.slice(0, 20) + '...',
        chainId,
        amount: amountInBaseUnits.toString()
      });
      
      // Send transaction - this should open the connected wallet
      txHash = await sendTransaction(wagmiAdapter.wagmiConfig, {
        to: tokenAddress as `0x${string}`,
        data,
        chainId
      });
    } else {
      // Native token transfer
      const amountInWei = isBaseUnits 
        ? BigInt(amount)
        : parseUnits(amount, 18);
      
      console.log('💰 Sending native transaction:', {
        to: toAddress,
        value: amountInWei.toString(),
        chainId
      });
      
      // Send transaction - this should open the connected wallet
      txHash = await sendTransaction(wagmiAdapter.wagmiConfig, {
        to: toAddress as `0x${string}`,
        value: amountInWei,
        chainId
      });
    }

    console.log('✅ Transaction sent, hash:', txHash);

    // Wait for transaction confirmation
    console.log('⏳ Waiting for transaction confirmation...');
    await waitForTransactionReceipt(wagmiAdapter.wagmiConfig, {
      hash: txHash as `0x${string}`,
      confirmations: 1,
    });

    console.log('✅ Transaction confirmed:', txHash);
    return txHash;
  } catch (error: any) {
    console.error('EVM transaction error:', error);
    
    // Parse error code
    if (error.code === 4001 || error.cause?.code === 4001) {
      throw new EVMTransactionError(
        'User rejected the transaction',
        'USER_REJECTED'
      );
    } else if (error.message?.includes('insufficient funds')) {
      throw new EVMTransactionError(
        'Insufficient funds for transaction',
        'INSUFFICIENT_FUNDS'
      );
    } else if (error.code === 'WRONG_CHAIN') {
      throw error; // Re-throw our own errors
    } else {
      throw new EVMTransactionError(
        error.message || 'Transaction failed',
        'UNKNOWN',
        error
      );
    }
  }
}